import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'lucide-react';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import SaveAnimatedIcon from '@/components/ui/SaveAnimatedIcon';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import { supabase } from '@/lib/supabase';
import SearchableSelectRaw from '@/components/ui/SearchableSelect';
import AsyncSearchableSelectRaw from '@/components/ui/AsyncSearchableSelect';
import ValorInputControlledRaw from '@/components/ui/ValorInputControlled';
import GenericFileRaw from '@/components/ui/GenericFile';
import ConfirmDeleteModalRaw from '@/components/ui/ConfirmDeleteModal';
import { formatDate } from '@/utils/formatters';
import { getErrorMessageString } from '@/services/errors';

// Raw-alias: sibling untyped — follow-up D.3.4/D.3.5
const SearchableSelect = SearchableSelectRaw as unknown as ComponentType<Record<string, unknown>>;
// Raw-alias: sibling untyped — follow-up D.3.4
const AsyncSearchableSelect = AsyncSearchableSelectRaw as unknown as ComponentType<Record<string, unknown>>;
// Raw-alias: sibling untyped — follow-up D.3.4
const ValorInputControlled = ValorInputControlledRaw as unknown as ComponentType<Record<string, unknown>>;
// Raw-alias: sibling already typed at hub boundary but shape uses superset Props — cast keeps this file self-contained
const GenericFile = GenericFileRaw as unknown as ComponentType<Record<string, unknown>>;
// Raw-alias: sibling untyped — follow-up D.3.4
const ConfirmDeleteModal = ConfirmDeleteModalRaw as unknown as ComponentType<Record<string, unknown>>;

// ==================== Companion types (file-local) ====================

export type ChildRow = Record<string, unknown> & {
  id?: string | number;
  _tempId?: string;
  _isPending?: boolean;
};

interface ChildColumn {
  field: string;
  label?: string;
  type?: string;
  width?: string;
  lookupKey?: string;
  options?: ChildFieldOption[];
  format?: (value: unknown, row: ChildRow) => ReactNode;
  [key: string]: unknown;
}

interface ChildFormField {
  field: string;
  label?: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  lookupKey?: string;
  options?: ChildFieldOption[];
  searchable?: boolean;
  asyncSearchable?: boolean;
  onSearch?: (query: string) => unknown;
  debounceMs?: number;
  minChars?: number;
  renderOption?: (opt: unknown) => ReactNode;
  renderValue?: (opt: unknown) => ReactNode;
  rows?: number;
  [key: string]: unknown;
}

interface ChildFieldOption {
  id?: string | number;
  value?: string | number;
  nome?: string;
  label?: string;
  [key: string]: unknown;
}

interface ChildrenSectionConfig {
  tableName?: string;
  parentIdField?: string;
  columns?: ChildColumn[];
  formFields?: ChildFormField[];
  defaultValues?: Record<string, unknown>;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  onChildrenChange?: (rows: ChildRow[]) => void;
  permissions?: ChildrenPermissions;
  /**
   * Overrides the default physical DELETE for persisted rows. Use when the row
   * must be removed through an RPC instead — e.g. `movimento`, whose soft-delete
   * cannot go through PostgREST (the select policy filters `deleted_at IS NULL`,
   * so `UPDATE ... RETURNING` re-checks it against the deleted row and 42501s).
   * Throw to surface a failure; the section reloads on success.
   */
  deleteHandler?: (item: ChildRow) => Promise<void>;
  /**
   * Overrides the default INSERT for new rows. Use when creation must go through
   * an RPC — e.g. `movimento`, which is created together with its difference
   * treatment in a single transaction, so a movement can never be persisted
   * without the cost that explains it. Throw to surface the message inline.
   */
  createHandler?: (data: Record<string, unknown>) => Promise<void>;
  /**
   * Extra content rendered inside the inline form, below the generated fields.
   * Receives the live form values so the extra block can validate against them.
   * The parent owns this state; use `onFormClose` to reset it.
   */
  renderFormExtra?: (ctx: {
    isEditing: boolean;
    formData: Record<string, unknown>;
  }) => ReactNode;
  /** Fires when the inline form closes (save or cancel), so the parent can reset. */
  onFormClose?: () => void;
  [key: string]: unknown;
}

interface ChildrenPermissions {
  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  [key: string]: unknown;
}

interface AttachmentsChildConfig {
  entityType: string;
  additionalEntityIdField?: string;
  recurso?: string;
  [key: string]: unknown;
}

interface PendingItems {
  toInsert: ChildRow[];
  toUpdate: Array<{ id: string | number; data: Record<string, unknown> }>;
  toDelete: Array<string | number>;
}

interface BulkEditDataMap {
  [itemKey: string]: ChildRow;
}

interface RenderItemHelpers {
  lookups: Record<string, ChildFieldOption[]>;
  bulkEditMode: boolean;
  bulkEditData: BulkEditDataMap;
  handleBulkFieldChange: (itemId: string | number, field: string, value: unknown) => void;
  handleDelete: (item: ChildRow) => void;
  formatValue: (column: ChildColumn, value: unknown, row: ChildRow) => ReactNode;
  renderBulkEditField: (column: ChildColumn, itemKey: string | number, value: unknown) => ReactNode;
  columns: ChildColumn[];
}

export interface ChildrenSectionHandle {
  getPendingItems: () => PendingItems;
  clearPending: () => void;
  hasPendingChanges: () => boolean;
  reload: () => Promise<void>;
  getAddButton: () => ReactNode;
  getCount: () => number;
  handleAdd: () => void;
  saveBulkEdit: () => Promise<{ success: boolean; errors?: Array<{ id: string | number; error: string }> }>;
  cancelBulkEdit: () => void;
  getBulkEditData: () => BulkEditDataMap;
}

export interface ChildrenSectionProps {
  config?: ChildrenSectionConfig;
  parentId?: string | number | null;
  parentData?: Record<string, unknown>;
  mode?: 'view' | 'edit' | 'create';
  lookups?: Record<string, ChildFieldOption[]>;
  permissions?: ChildrenPermissions;
  canAddCondition?: (parentData: Record<string, unknown>, children: ChildRow[]) => boolean;
  canEditCondition?: (item: ChildRow, parentData: Record<string, unknown>) => boolean;
  canDeleteCondition?: (item: ChildRow, parentData: Record<string, unknown>) => boolean;
  allowEditInViewMode?: boolean;
  batchSaveMode?: boolean;
  onPendingChange?: (pending: PendingItems) => void;
  showLabels?: boolean;
  onExpand?: () => void;
  attachmentsConfig?: AttachmentsChildConfig;
  bulkEditMode?: boolean;
  hideIndividualEditButton?: boolean;
  showDeleteOnlyInBulkEdit?: boolean;
  renderItem?: (item: ChildRow, helpers: RenderItemHelpers) => ReactNode;
}

// Supabase dynamic-table bypass (mold B.2)
const dynDb = supabase as unknown as {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (col: string, val: unknown) => {
        order: (col: string, opts: { ascending: boolean }) => Promise<{ data: ChildRow[] | null; error: { message: string } | null }>;
      } & Promise<{ data: ChildRow[] | null; error: { message: string } | null }>;
    };
    update: (data: Record<string, unknown>) => {
      eq: (col: string, val: unknown) => Promise<{ error: { message: string } | null }>;
    };
    insert: (rows: Array<Record<string, unknown>>) => Promise<{ error: { message: string } | null }>;
    delete: () => {
      eq: (col: string, val: unknown) => Promise<{ error: { message: string } | null }>;
    };
  };
};

const ChildrenSection = forwardRef<ChildrenSectionHandle, ChildrenSectionProps>(function ChildrenSection({
  config = {},
  parentId,
  parentData = {},
  mode = 'view',
  lookups = {},
  permissions = {},
  canAddCondition,
  canEditCondition,
  canDeleteCondition,
  allowEditInViewMode = false,
  batchSaveMode = false,
  onPendingChange,
  showLabels = true,
  onExpand,
  attachmentsConfig,
  bulkEditMode = false,
  hideIndividualEditButton = false,
  showDeleteOnlyInBulkEdit = false,
  renderItem,
}, ref) {
  const {
    tableName,
    parentIdField,
    columns = [],
    formFields = [],
    defaultValues = {},
    orderBy = 'created_at',
    orderDirection = 'desc',
    onChildrenChange,
  } = config;

  const [children, setChildren] = useState<ChildRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<ChildRow | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<boolean>(false);

  const [pendingItems, setPendingItems] = useState<PendingItems>({
    toInsert: [],
    toUpdate: [],
    toDelete: [],
  });

  const [expandedAttachments, setExpandedAttachments] = useState<string | number | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<ChildRow | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [bulkEditData, setBulkEditData] = useState<BulkEditDataMap>({});
  const [originalData, setOriginalData] = useState<ChildRow[]>([]);

  useEffect(() => {
    if (bulkEditMode && children.length > 0) {
      const editData: BulkEditDataMap = {};
      children.forEach(item => {
        const key = String(item.id ?? item._tempId ?? '');
        editData[key] = { ...item };
      });
      setBulkEditData(editData);
      setOriginalData(JSON.parse(JSON.stringify(children)) as ChildRow[]);
    } else if (!bulkEditMode) {
      setBulkEditData({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkEditMode, children.length]);

  const handleBulkFieldChange = useCallback((itemId: string | number, field: string, value: unknown) => {
    setBulkEditData(prev => ({
      ...prev,
      [String(itemId)]: {
        ...(prev[String(itemId)] ?? {}),
        [field]: value,
      },
    }));
  }, []);

  const isDrawerEditing = mode === 'edit' || mode === 'create';
  const allowActions = isDrawerEditing || allowEditInViewMode;

  const canAddBase = permissions.canAdd !== false && allowActions;
  const canAdd = canAddBase && (
    !canAddCondition || canAddCondition(parentData, children)
  );

  const canEditBase = permissions.canEdit !== false && allowActions;
  const canDeleteBase = permissions.canDelete !== false && allowActions;

  const canEditItem = useCallback((item: ChildRow): boolean => {
    if (!canEditBase) return false;
    if (!canEditCondition) return true;
    return canEditCondition(item, parentData);
  }, [canEditBase, canEditCondition, parentData]);

  const canDeleteItem = useCallback((item: ChildRow): boolean => {
    if (!canDeleteBase) return false;
    if (!canDeleteCondition) return true;
    return canDeleteCondition(item, parentData);
  }, [canDeleteBase, canDeleteCondition, parentData]);

  const handleAdd = useCallback(() => {
    if (onExpand) onExpand();
    setEditingItem(null);
    setFormData({ ...defaultValues });
    setErrors({});
    setShowForm(true);
  }, [defaultValues, onExpand]);

  const AddButton: ReactNode = canAdd ? (
    <button className="btn-add cs-plus-animated" onClick={handleAdd}>
      <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <defs>
          <linearGradient id="csPlusG" gradientUnits="userSpaceOnUse" x1="5" y1="12" x2="19" y2="5">
            <stop offset="0%" stopColor="#154680" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
        </defs>
        <g className="cs-plus-stroke" stroke="url(#csPlusG)">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </g>
      </svg>
      <span className="btn-text">Adicionar</span>
    </button>
  ) : null;

  const loadChildren = useCallback(async (): Promise<void> => {
    if (!tableName || !parentId || !parentIdField) return;

    setLoading(true);
    try {
      const query = dynDb
        .from(tableName)
        .select('*')
        .eq(parentIdField, parentId);

      const finalQuery = orderBy
        ? query.order(orderBy, { ascending: orderDirection === 'asc' })
        : query;

      const { data, error } = await finalQuery;

      if (error) throw new Error(error.message);
      const rows = data ?? [];
      setChildren(rows);

      if (onChildrenChange) {
        onChildrenChange(rows);
      }
    } catch (err) {
      console.error('[ChildrenSection] Erro ao carregar:', err);
    } finally {
      setLoading(false);
    }
  }, [tableName, parentId, parentIdField, orderBy, orderDirection, onChildrenChange]);

  const saveBulkEdit = useCallback(async (): Promise<{ success: boolean; errors?: Array<{ id: string | number; error: string }> }> => {
    const updates: Array<{ id: string | number; data: Record<string, unknown> }> = [];
    const localErrors: Array<{ id: string | number; error: string }> = [];

    for (const [itemId, itemDataRaw] of Object.entries(bulkEditData)) {
      const itemData = itemDataRaw as ChildRow;
      const originalItem = children.find(c => String(c.id ?? c._tempId ?? '') === itemId || String(c.id ?? '') === itemId);
      if (!originalItem) continue;

      const hasChanges = columns.some(col => {
        return itemData[col.field] !== originalItem[col.field];
      });

      if (hasChanges && originalItem.id !== undefined) {
        const updateData: Record<string, unknown> = {};
        columns.forEach(col => {
          if (itemData[col.field] !== undefined) {
            updateData[col.field] = itemData[col.field];
          }
        });
        formFields.forEach(ff => {
          if (itemData[ff.field] !== undefined && !columns.find(c => c.field === ff.field)) {
            updateData[ff.field] = itemData[ff.field];
          }
        });
        updates.push({ id: originalItem.id, data: updateData });
      }
    }

    if (!tableName) return { success: false, errors: [] };

    for (const update of updates) {
      try {
        const { error } = await dynDb
          .from(tableName)
          .update(update.data)
          .eq('id', update.id);

        if (error) {
          localErrors.push({ id: update.id, error: error.message });
        }
      } catch (err) {
        localErrors.push({ id: update.id, error: getErrorMessageString(err) });
      }
    }

    if (localErrors.length > 0) {
      console.error('[ChildrenSection] Erros ao salvar bulk edit:', localErrors);
      return { success: false, errors: localErrors };
    }

    await loadChildren();
    setBulkEditData({});
    return { success: true };
  }, [bulkEditData, children, columns, formFields, tableName, loadChildren]);

  const cancelBulkEdit = useCallback(() => {
    setBulkEditData({});
    if (originalData.length > 0) {
      setChildren(originalData);
    }
  }, [originalData]);

  useImperativeHandle(ref, () => ({
    getPendingItems: () => pendingItems,
    clearPending: () => {
      setPendingItems({ toInsert: [], toUpdate: [], toDelete: [] });
    },
    hasPendingChanges: () => {
      return pendingItems.toInsert.length > 0 ||
             pendingItems.toUpdate.length > 0 ||
             pendingItems.toDelete.length > 0;
    },
    reload: loadChildren,
    getAddButton: () => AddButton,
    getCount: () => children.length,
    handleAdd,
    saveBulkEdit,
    cancelBulkEdit,
    getBulkEditData: () => bulkEditData,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pendingItems, AddButton, children.length, handleAdd, saveBulkEdit, cancelBulkEdit, bulkEditData, loadChildren]);

  useEffect(() => {
    if (parentId && tableName) {
      void loadChildren();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, tableName]);

  const handleEdit = (item: ChildRow) => {
    if (onExpand) onExpand();
    setEditingItem(item);
    setFormData({ ...item });
    setErrors({});
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({});
    setErrors({});
    config.onFormClose?.();
  };

  useEffect(() => {
    if (batchSaveMode && onPendingChange) {
      onPendingChange(pendingItems);
    }
  }, [pendingItems, batchSaveMode, onPendingChange]);

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    formFields.forEach(field => {
      if (field.required && !formData[field.field]) {
        newErrors[field.field] = `${field.label ?? field.field} é obrigatório`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const processedFormData: Record<string, unknown> = { ...formData };
    formFields.forEach(field => {
      if (field.asyncSearchable && processedFormData[field.field]) {
        const fieldValue = processedFormData[field.field];
        if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
          processedFormData[field.field] = (fieldValue as { value: unknown }).value;
        }
      }
    });

    if (batchSaveMode) {
      if (editingItem) {
        if (editingItem._isPending) {
          const tempId = editingItem._tempId;
          setPendingItems(prev => ({
            ...prev,
            toInsert: prev.toInsert.map(item =>
              item._tempId === tempId ? { ...processedFormData, _tempId: tempId, _isPending: true } : item
            ),
          }));
          setChildren(prev => prev.map(item =>
            item._tempId === tempId ? { ...processedFormData, _tempId: tempId, _isPending: true } : item
          ));
        } else {
          const editingId = editingItem.id;
          if (editingId !== undefined) {
            setPendingItems(prev => ({
              ...prev,
              toUpdate: [
                ...prev.toUpdate.filter(u => u.id !== editingId),
                { id: editingId, data: processedFormData },
              ],
            }));
            setChildren(prev => prev.map(item =>
              item.id === editingId ? { ...item, ...processedFormData } : item
            ));
          }
        }
      } else {
        const tempId = `_temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        const newItem: ChildRow = { ...processedFormData, _tempId: tempId, _isPending: true };
        setPendingItems(prev => ({
          ...prev,
          toInsert: [...prev.toInsert, newItem],
        }));
        setChildren(prev => [...prev, newItem]);
      }
      handleCancel();
      return;
    }

    if (!tableName) return;

    setSaving(true);
    try {
      if (editingItem && editingItem.id !== undefined) {
        const { error } = await dynDb
          .from(tableName)
          .update(processedFormData)
          .eq('id', editingItem.id);

        if (error) throw new Error(error.message);
      } else {
        const dataToInsert: Record<string, unknown> = {
          ...processedFormData,
          [parentIdField ?? 'parent_id']: parentId,
        };
        delete dataToInsert.id;

        if (config.createHandler) {
          await config.createHandler(dataToInsert);
        } else {
          const { error } = await dynDb
            .from(tableName)
            .insert([dataToInsert]);

          if (error) throw new Error(error.message);
        }
      }

      await loadChildren();
      handleCancel();
    } catch (err) {
      console.error('[ChildrenSection] Erro ao salvar:', err);
      setErrors({ submit: getErrorMessageString(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: ChildRow) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    if (batchSaveMode) {
      if (itemToDelete._isPending) {
        const tempId = itemToDelete._tempId;
        setPendingItems(prev => ({
          ...prev,
          toInsert: prev.toInsert.filter(i => i._tempId !== tempId),
        }));
      } else if (itemToDelete.id !== undefined) {
        const deleteId = itemToDelete.id;
        setPendingItems(prev => ({
          ...prev,
          toDelete: [...prev.toDelete, deleteId],
          toUpdate: prev.toUpdate.filter(u => u.id !== deleteId),
        }));
      }
      setChildren(prev => prev.filter(i =>
        itemToDelete._isPending ? i._tempId !== itemToDelete._tempId : i.id !== itemToDelete.id
      ));
      handleCancelDelete();
      return;
    }

    if (!tableName || itemToDelete.id === undefined) return;

    setDeleting(true);
    try {
      if (config.deleteHandler) {
        await config.deleteHandler(itemToDelete);
      } else {
        const { error } = await dynDb
          .from(tableName)
          .delete()
          .eq('id', itemToDelete.id);

        if (error) throw new Error(error.message);
      }
      await loadChildren();
      handleCancelDelete();
    } catch (err) {
      console.error('[ChildrenSection] Erro ao excluir:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatValue = (column: ChildColumn, value: unknown, row: ChildRow): ReactNode => {
    if (column.format) {
      return column.format(value, row);
    }

    if (value === null || value === undefined) return '-';

    switch (column.type) {
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(typeof value === 'number' ? value : Number(value));

      case 'date':
        return formatDate(value as string);

      case 'select':
        if (column.lookupKey && lookups[column.lookupKey]) {
          const option = lookups[column.lookupKey].find(o => o.id === value);
          return option?.nome ?? option?.label ?? String(value);
        }
        return String(value);

      default:
        return String(value);
    }
  };

  const renderBulkEditField = (column: ChildColumn, itemKey: string | number, value: unknown): ReactNode => {
    const baseInputStyle: CSSProperties = {
      width: '100%',
      padding: '6px 10px',
      fontSize: '13px',
      border: '1px solid var(--panel-border)',
      borderRadius: '4px',
      background: 'white',
      minWidth: column.type === 'number' ? '60px' : '120px',
    };

    const fieldConfig = formFields.find(f => f.field === column.field) ?? (column as unknown as ChildFormField);

    switch (column.type) {
      case 'currency':
        return (
          <ValorInputControlled
            value={value || 0}
            onChange={(numValue: unknown) => handleBulkFieldChange(itemKey, column.field, numValue)}
            showPrefix={false}
            style={{ fontSize: '13px', minWidth: '100px' }}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={(value as string | number | undefined) ?? ''}
            onChange={(e) => handleBulkFieldChange(itemKey, column.field, e.target.value ? Number(e.target.value) : null)}
            style={{ ...baseInputStyle, maxWidth: '80px' }}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={(value as string | undefined) ?? ''}
            onChange={(e) => handleBulkFieldChange(itemKey, column.field, e.target.value)}
            style={baseInputStyle}
          />
        );

      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => handleBulkFieldChange(itemKey, column.field, e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
        );

      case 'select': {
        const options: ChildFieldOption[] = column.lookupKey && lookups[column.lookupKey]
          ? lookups[column.lookupKey]
          : column.options ?? [];

        if (fieldConfig.searchable || options.length > 10) {
          const selectOptions = options.map(o => ({
            value: o.id ?? o.value,
            label: o.nome ?? o.label,
          }));
          const selectedOption = selectOptions.find(o => o.value === value);

          return (
            <div style={{ minWidth: '150px' }}>
              <SearchableSelect
                options={selectOptions}
                value={selectedOption ?? null}
                onChange={(opt: unknown) => {
                  const v = opt && typeof opt === 'object' && 'value' in opt ? (opt as { value: unknown }).value : null;
                  handleBulkFieldChange(itemKey, column.field, v ?? null);
                }}
                placeholder="Selecione..."
                isClearable={true}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  control: (base: CSSProperties) => ({ ...base, minHeight: '32px', fontSize: '13px' }),
                  menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          );
        }

        return (
          <select
            value={(value as string | number | undefined) ?? ''}
            onChange={(e) => handleBulkFieldChange(itemKey, column.field, e.target.value || null)}
            style={{ ...baseInputStyle, maxWidth: '150px' }}
          >
            <option value="">Selecione...</option>
            {options.map(opt => (
              <option key={String(opt.id ?? opt.value)} value={String(opt.id ?? opt.value ?? '')}>
                {opt.nome ?? opt.label}
              </option>
            ))}
          </select>
        );
      }

      default:
        return (
          <input
            type="text"
            value={(value as string | undefined) ?? ''}
            onChange={(e) => handleBulkFieldChange(itemKey, column.field, e.target.value)}
            style={baseInputStyle}
          />
        );
    }
  };

  const renderFormField = (field: ChildFormField): ReactNode => {
    const value = formData[field.field];
    const error = errors[field.field];

    const baseInputStyle: CSSProperties = {
      width: '100%',
      padding: '8px 12px',
      fontSize: '13px',
      border: `1px solid ${error ? 'var(--color-danger)' : 'var(--panel-border)'}`,
      borderRadius: '6px',
      background: 'white',
    };

    switch (field.type) {
      case 'currency':
        return (
          <ValorInputControlled
            value={value || 0}
            onChange={(numValue: unknown) => setFormData(prev => ({
              ...prev,
              [field.field]: numValue,
            }))}
            hasError={!!error}
            showPrefix={false}
            style={{ fontSize: '13px' }}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={(value as string | undefined) ?? ''}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.field]: e.target.value }))}
            style={baseInputStyle}
          />
        );

      case 'select': {
        const options: ChildFieldOption[] = field.lookupKey && lookups[field.lookupKey]
          ? lookups[field.lookupKey]
          : field.options ?? [];

        if (field.asyncSearchable && field.onSearch) {
          return (
            <AsyncSearchableSelect
              value={value}
              onChange={(opt: unknown) => setFormData(prev => ({ ...prev, [field.field]: opt ?? null }))}
              onSearch={field.onSearch}
              placeholder={field.placeholder ?? 'Digite para buscar...'}
              debounceMs={field.debounceMs ?? 300}
              minChars={field.minChars ?? 0}
              renderOption={field.renderOption}
              renderValue={field.renderValue}
            />
          );
        }

        if (field.searchable || options.length > 10) {
          const selectOptions = options.map(o => ({
            value: o.id ?? o.value,
            label: o.nome ?? o.label,
          }));
          const selectedOption = selectOptions.find(o => o.value === value);

          return (
            <SearchableSelect
              options={selectOptions}
              value={selectedOption ?? null}
              onChange={(opt: unknown) => {
                const v = opt && typeof opt === 'object' && 'value' in opt ? (opt as { value: unknown }).value : null;
                setFormData(prev => ({ ...prev, [field.field]: v ?? null }));
              }}
              placeholder={field.placeholder ?? 'Selecione...'}
              isClearable={!field.required}
            />
          );
        }

        return (
          <select
            value={(value as string | number | undefined) ?? ''}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.field]: e.target.value || null }))}
            style={baseInputStyle}
          >
            <option value="">{field.placeholder ?? 'Selecione...'}</option>
            {options.map(opt => (
              <option key={String(opt.id ?? opt.value)} value={String(opt.id ?? opt.value ?? '')}>
                {opt.nome ?? opt.label}
              </option>
            ))}
          </select>
        );
      }

      case 'textarea':
        return (
          <textarea
            value={(value as string | undefined) ?? ''}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.field]: e.target.value }))}
            rows={field.rows ?? 2}
            style={{ ...baseInputStyle, resize: 'vertical' }}
          />
        );

      default:
        return (
          <input
            type={field.type ?? 'text'}
            value={(value as string | undefined) ?? ''}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.field]: e.target.value }))}
            placeholder={field.placeholder}
            style={baseInputStyle}
          />
        );
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      {showForm && (
        <div style={{
          background: '#fafafa',
          border: '1px solid var(--panel-border)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '16px',
          }}>
            {formFields.map(field => (
              <div key={field.field}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '4px',
                }}>
                  {field.label}{field.type === 'currency' ? ' (R$)' : ''}
                  {field.required && <span className="required-asterisk">*</span>}
                </label>
                {renderFormField(field)}
                {errors[field.field] && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--danger)' }}>
                    {errors[field.field]}
                  </p>
                )}
              </div>
            ))}
          </div>

          {config.renderFormExtra?.({ isEditing: editingItem != null, formData })}

          {errors.submit && (
            <p style={{ margin: '12px 0', fontSize: '12px', color: 'var(--danger)' }}>
              {errors.submit}
            </p>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleCancel}
              className="btn-add undo-animated"
              style={{ height: '28px', padding: '0 12px', fontSize: '12px' }}
            >
              <UndoAnimatedIcon size={14} />
              <span className="btn-text">Cancelar</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-add save-animated"
              style={{ height: '28px', padding: '0 12px', fontSize: '12px' }}
            >
              <SaveAnimatedIcon size={14} />
              <span className="btn-text">{saving ? 'Salvando...' : 'Salvar'}</span>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes csClipSwing {
          0%   { transform: rotate(0deg); }
          20%  { transform: rotate(14deg); }
          40%  { transform: rotate(-10deg); }
          60%  { transform: rotate(6deg); }
          80%  { transform: rotate(-3deg); }
          100% { transform: rotate(0deg); }
        }
        .cs-clip-animated .cs-clip-body {
          transform-origin: center;
          transform-box: fill-box;
        }
        .cs-clip-animated:hover .cs-clip-body {
          animation: csClipSwing 0.6s cubic-bezier(0.34,1.56,0.64,1);
        }
        .cs-clip-animated.active {
          background: rgba(21, 70, 128, 0.08) !important;
        }
        @keyframes csPlusRotate {
          0% { transform: rotate(0deg) scale(1); }
          40% { transform: rotate(90deg) scale(1.2); }
          100% { transform: rotate(90deg) scale(1); }
        }
        .cs-plus-animated .cs-plus-stroke {
          transform-origin: center;
          transform-box: fill-box;
        }
        .cs-plus-animated:hover .cs-plus-stroke {
          animation: csPlusRotate 0.4s cubic-bezier(0.34,1.56,0.64,1);
        }
      `}</style>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>
          Carregando...
        </div>
      ) : children.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: 'var(--muted)',
          background: '#fafafa',
          borderRadius: '8px',
        }}>
          Nenhum item cadastrado
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {children.map((item) => {
            const itemCanEdit = canEditItem(item);
            const itemCanDelete = canDeleteItem(item);
            const showActions = (canEditBase || canDeleteBase) && (itemCanEdit || itemCanDelete);
            const itemKeyRaw = item.id ?? item._tempId ?? '';
            const isAttachmentsExpanded = expandedAttachments === itemKeyRaw;
            const hasAttachmentsConfig = !!attachmentsConfig && item.id !== undefined;

            return (
              <div
                key={String(itemKeyRaw)}
                style={{
                  background: 'white',
                  border: '1px solid var(--panel-border)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.15s ease',
                }}
              >
                <div
                  style={{
                    padding: showLabels ? '12px 16px' : '8px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: isAttachmentsExpanded ? '#fafafa' : 'white',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isAttachmentsExpanded) {
                      e.currentTarget.style.background = '#fafafa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isAttachmentsExpanded) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  {renderItem ? (
                    <div style={{ flex: 1 }}>
                      {renderItem(item, {
                        lookups,
                        bulkEditMode,
                        bulkEditData,
                        handleBulkFieldChange,
                        handleDelete,
                        formatValue,
                        renderBulkEditField,
                        columns,
                      })}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: bulkEditMode ? '12px' : '24px', flex: 1 }}>
                      {columns.map(col => {
                        const itemKey = (item.id ?? item._tempId ?? '') as string | number;
                        const bulkDataForItem = bulkEditMode ? bulkEditData[String(itemKey)] : undefined;
                        const currentValue = bulkDataForItem
                          ? bulkDataForItem[col.field]
                          : item[col.field];

                        return (
                          <div key={col.field} style={{ display: 'flex', flexDirection: 'column', gap: showLabels ? '2px' : '0', flex: bulkEditMode ? 1 : 'none' }}>
                            {showLabels && (
                              <span style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {col.label}
                              </span>
                            )}
                            {bulkEditMode ? (
                              renderBulkEditField(col, itemKey, currentValue)
                            ) : (
                              <span style={{ fontSize: '13px', color: '#111827' }}>
                                {formatValue(col, item[col.field], item)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '4px', marginLeft: '16px' }}>
                    {hasAttachmentsConfig && (
                      <button
                        className={`btn-edit-icon cs-clip-animated${isAttachmentsExpanded ? ' active' : ''}`}
                        onClick={() => setExpandedAttachments(isAttachmentsExpanded ? null : ((item.id ?? item._tempId ?? null) as string | number | null))}
                        title="Anexos"
                        style={{ height: '28px', width: '28px' }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <defs>
                            <linearGradient id="csClipG" gradientUnits="userSpaceOnUse" x1="4" y1="22" x2="22" y2="4">
                              <stop offset="0%" stopColor="#154680" />
                              <stop offset="100%" stopColor="#3b82f6" />
                            </linearGradient>
                          </defs>
                          <g className="cs-clip-body" stroke="url(#csClipG)">
                            <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                          </g>
                        </svg>
                        <ChevronDown
                          size={12}
                          style={{
                            color: 'var(--muted)',
                            transition: 'transform 0.2s ease',
                            transform: isAttachmentsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </button>
                    )}
                    {showActions && itemCanEdit && !hideIndividualEditButton && !bulkEditMode && (
                      <button
                        className="btn-edit-icon gf-pen-animated"
                        onClick={() => handleEdit(item)}
                        title="Editar"
                        style={{ height: '28px', width: '28px' }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <defs>
                            <linearGradient id="csEpG" gradientUnits="userSpaceOnUse" x1="3" y1="0" x2="21" y2="0">
                              <stop offset="0%" stopColor="#60a5fa" />
                              <stop offset="100%" stopColor="#154680" />
                            </linearGradient>
                          </defs>
                          <g className="pen-square" stroke="url(#csEpG)">
                            <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          </g>
                          <g className="pen-tip" stroke="url(#csEpG)">
                            <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/>
                          </g>
                        </svg>
                      </button>
                    )}
                    {showActions && itemCanDelete && (!showDeleteOnlyInBulkEdit || bulkEditMode) && (
                      <button
                        className="btn-delete-icon gf-trash-animated"
                        onClick={() => handleDelete(item)}
                        title="Excluir"
                        style={{ height: '28px', width: '28px' }}
                      >
                        <TrashAnimatedIcon size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {hasAttachmentsConfig && isAttachmentsExpanded && attachmentsConfig && (
                  <div style={{
                    borderTop: '1px solid var(--panel-border)',
                    padding: '12px 16px',
                    background: '#fafafa',
                  }}>
                    <GenericFile
                      entityId={item.id}
                      entityType={attachmentsConfig.entityType}
                      additionalEntityId={attachmentsConfig.additionalEntityIdField ? parentData[attachmentsConfig.additionalEntityIdField] : undefined}
                      recurso={attachmentsConfig.recurso}
                      compact={true}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={confirmDelete}
        title="Excluir item"
        loading={deleting}
      >
        <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
          Tem certeza que deseja excluir este item?
        </p>
      </ConfirmDeleteModal>
    </div>
  );
});

export default ChildrenSection;
